import React, { ReactElement, useState } from 'react'
import { Link } from 'react-router'

import paths from '../../constants/paths'
import { validateEmail } from '../../utils/email-utils'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { setByPath } from '../../utils/object-utils'
import { t, tx } from '../../utils/translation-utils'
import { trimSpaces } from '../../utils/validation-utils'
import Form from '../antd/form'
import Button from '../elements/button'
import Checkbox from '../elements/checkbox'
import Input from '../elements/input'
import Modal from '../elements/modal'
import PhoneNumberForm from '../form-elements/PhoneNumberForm'
import DumbLink from '../widgets/DumbLink'
import UserTerms from '../widgets/UserTerms'

type Props = {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  phoneNumberCountryCode?: string
  email?: string
  emailRepeat?: string
  password?: string
  terms: boolean
}

type Fields = {
  firstName?: string
  lastName?: string
  phoneNumber?: string
  phoneNumberCountryCode?: string
  email?: string
  emailRepeat?: string
  password?: string
  terms: boolean
}

export type RegisterResult = {
  name: string
  phoneNumber: string
  phoneNumberCountryCode: string
  email: string
  password: string
  terms: boolean
}

const NAME_REGEX = /^[\p{L}\s\-'.]+$/u

const validateNamePart = (val: string | undefined, requiredKey: string): string | null => {
  if (!val || val.trim().length === 0) {
    return t(requiredKey)
  }
  if (val.trim().length < 2) {
    return t('register.form.name.too_short')
  }
  if (!NAME_REGEX.test(val.trim())) {
    return t('register.form.name.invalid_characters')
  }
  return null
}

function RegisterForm(props: Props & FormComponentProps<Fields, RegisterResult>): ReactElement | null {
  const [showTerms, setShowTerms] = useState(false)

  const { decorateField, getFieldValue, getFieldError } = props

  return (
    <div>
      {decorateField('firstName', {
        placeholder: t('register.form.first_name'),
        validate: (val) => validateNamePart(val, 'register.form.first_name.required'),
      })(<Input tabIndex={1} />)}
      {decorateField('lastName', {
        placeholder: t('register.form.last_name'),
        validate: (val) => validateNamePart(val, 'register.form.last_name.required'),
      })(<Input tabIndex={2} />)}
      <PhoneNumberForm
        decorateField={decorateField}
        getFieldValue={getFieldValue}
        getFieldError={getFieldError}
        tabIndex={3}
        requirePhoneNumber
      />
      {decorateField('email', {
        placeholder: t('register.form.email'),
        validate: (val) => {
          if (!val) {
            return t('register.form.email.required')
          }
          if (!validateEmail(val)) {
            return t('register.form.email.invalid')
          }
          return null
        },
      })(<Input tabIndex={4} />)}
      {decorateField('emailRepeat', {
        placeholder: t('register.form.email_repeat'),
        validate: (val) => {
          if (!val) {
            return t('register.form.email_repeat.required')
          }
          if (val !== getFieldValue('email')) {
            return t('register.form.email_repeat.mismatch')
          }
          return null
        },
      })(<Input tabIndex={5} />)}
      {decorateField('password', {
        placeholder: t('register.form.password'),
        validate: (val) => {
          if (!val) {
            return t('register.form.password.required')
          }
          if (val.length < 8) {
            return t('register.form.password.at_least_8_characters')
          }
          return null
        },
      })(<Input type="password" tabIndex={6} />)}
      <Form.Item validateStatus={props.getFieldError('terms') ? 'error' : 'success'}>
        {decorateField('terms', {
          validate: (val) => {
            if (!val) {
              return t('register.form.terms.required')
            }
            return null
          },
          skipWrapper: true,
          skipLabel: true,
        })(
          <Checkbox tabIndex={7} id="checkbox-accept-terms">
            {tx('register.form.terms', {
              terms: (
                <DumbLink
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    setShowTerms(true)
                  }}
                >
                  {t('register.form.terms.terms')}
                </DumbLink>
              ),
              privacy: (
                <a href="https://salary.dk/privacy-policy/" target="_blank" rel="noreferrer">
                  {t('register.form.terms.privacy')}
                </a>
              ),
            })}
          </Checkbox>
        )}
        <Modal
          visible={showTerms}
          onOk={() => setShowTerms(false)}
          onCancel={() => setShowTerms(false)}
          width={579}
          footer={null}
        >
          <UserTerms setVisibility={setShowTerms} />
        </Modal>

        <Button htmlType="submit" type="primary" id="btn-register-user" tabIndex={8}>
          {t('register.form.submit')}
        </Button>
        <p style={{ marginTop: '15px', marginBottom: '0' }}>
          {tx('register.form.already_user', {
            link: <Link to={'/' + paths.LOGIN + document.location.search}>{t('register.form.already_user.link')}</Link>,
          })}
        </p>
      </Form.Item>
    </div>
  )
}

export default withValidations<Props, Fields, RegisterResult>({
  mapPropsToFields: (props) => ({
    firstName: props.firstName,
    lastName: props.lastName,
    phoneNumber: props.phoneNumber,
    phoneNumberCountryCode: props.phoneNumberCountryCode,
    email: props.email,
    emailRepeat: props.emailRepeat,
    password: props.password,
    terms: props.terms,
  }),
  onChange: (key, val) => {
    const values: Partial<Fields> = {}
    switch (key) {
      case 'phoneNumber':
        setByPath(values, key, trimSpaces(val as string))
        break
      default:
        setByPath(values, key, val)
        break
    }
    return values
  },
  onSubmit: (values) => ({
    ...values,
    name: `${values.firstName!.trim()} ${values.lastName!.trim()}`,
    phoneNumber: values.phoneNumber!,
    phoneNumberCountryCode: values.phoneNumberCountryCode!,
    email: values.email!,
    password: values.password!,
  }),
})(RegisterForm)
